'use client';

import { useMemo } from 'react';
import { useOrderStore } from '@/store/useOrderStore';
import { calculateMultiItemDiscounts, calcFlexDiscount, calcDeliveryTimeSurcharge, calcSlotDiscount, calcFinalPrice } from '@/lib/price-calculator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export default function OrderSummary() {
  const {
    itemInfo,
    items,
    origin,
    destination,
    priceBreakdown,
    scheduleDate,
    designatedPickupSlot,
    designatedDeliverySlot,
    flexiblePickupMorning,
    flexiblePickupAfternoon,
    pickupDiscountRate,
    deliveryDiscountRate,
  } = useOrderStore();

  // 모든 물품 = 확정된 items + 현재 편집 중인 itemInfo (있으면)
  const allItems = [
    ...items,
    ...(itemInfo.itemId
      ? [{ uid: '_current', displayName: itemInfo.category || '물품', basePrice: itemInfo.basePrice }]
      : []),
  ];

  // 다물품 할인 계산
  const discountMap = useMemo(() => calculateMultiItemDiscounts(allItems), [allItems]);

  const flexCount =
    (flexiblePickupMorning ? 1 : 0) +
    (flexiblePickupAfternoon ? 1 : 0);
  const hasDesignatedDelivery = !!designatedDeliverySlot;

  const formatPrice = (price: number) => price.toLocaleString() + '원';

  const getScheduleLabel = () => {
    const parts: string[] = [];
    if (designatedPickupSlot) parts.push(`수거 ${designatedPickupSlot}`);
    if (designatedDeliverySlot) parts.push(`배송 ${designatedDeliverySlot}`);
    return parts.length > 0 ? parts.join(' / ') : null;
  };

  return (
    <Card className="sticky top-20 border-gray-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-bold text-[#1A3A5C]">주문 요약</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 물품 정보 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">선택 물품</div>
          {allItems.length > 0 ? (
            <div className="space-y-1">
              {allItems.map((item) => {
                const disc = discountMap.get(item.uid);
                const hasDiscount = disc && disc.discountRate > 0;
                return (
                  <div key={item.uid} className="flex justify-between items-center">
                    <span className="font-medium text-gray-900 text-sm">{item.displayName}</span>
                    {hasDiscount ? (
                      <span className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 line-through">{formatPrice(item.basePrice)}</span>
                        <span className="text-xs bg-red-100 text-red-600 px-1 py-0.5 rounded-full font-medium">
                          {Math.round(disc.discountRate * 100)}%
                        </span>
                        <span className="text-sm text-[#4DA3FF]">{formatPrice(disc.discountedPrice)}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-[#4DA3FF]">{formatPrice(item.basePrice)}</span>
                    )}
                  </div>
                );
              })}
              {allItems.length >= 2 && (
                <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                  <span className="text-xs text-gray-500">물품 소계</span>
                  <span className="text-sm font-medium text-[#4DA3FF]">
                    {formatPrice(
                      allItems.reduce((sum, i) => {
                        const d = discountMap.get(i.uid);
                        return sum + (d ? d.discountedPrice : i.basePrice);
                      }, 0),
                    )}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="font-medium text-gray-400">
              {itemInfo.isCustom ? '커스텀 물품 (견적 요청)' : '물품을 선택해주세요'}
            </div>
          )}
        </div>

        <Separator />

        {/* 경로 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">배송 경로</div>
          {origin && destination ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-700 truncate">
                  {origin.address.split(' ').slice(1, 3).join(' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-sm text-gray-700 truncate">
                  {destination.address.split(' ').slice(1, 3).join(' ')}
                </span>
              </div>
            </div>
          ) : (
            <div className="font-medium text-gray-400">주소를 입력해주세요</div>
          )}
        </div>

        <Separator />

        {/* 운송비 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">예상 운송비</div>
          {priceBreakdown ? (
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">물품 운송비</span>
                <span>{formatPrice(priceBreakdown.itemBasePrice)}</span>
              </div>
              {priceBreakdown.distanceSurcharge > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">거리할증</span>
                  <span>+{formatPrice(priceBreakdown.distanceSurcharge)}</span>
                </div>
              )}
              {priceBreakdown.stairSurchargeOrigin > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">계단할증 (출발지)</span>
                  <span>+{formatPrice(priceBreakdown.stairSurchargeOrigin)}</span>
                </div>
              )}
              {priceBreakdown.stairSurchargeDest > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">계단할증 (도착지)</span>
                  <span>+{formatPrice(priceBreakdown.stairSurchargeDest)}</span>
                </div>
              )}
              {calcFlexDiscount(priceBreakdown.totalPrice, flexCount) > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>유연시간 할인</span>
                  <span>-{formatPrice(calcFlexDiscount(priceBreakdown.totalPrice, flexCount))}</span>
                </div>
              )}
              {calcSlotDiscount(priceBreakdown.totalPrice, pickupDiscountRate, deliveryDiscountRate) > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>최적시간 할인</span>
                  <span>-{formatPrice(calcSlotDiscount(priceBreakdown.totalPrice, pickupDiscountRate, deliveryDiscountRate))}</span>
                </div>
              )}
              {calcDeliveryTimeSurcharge(priceBreakdown.totalPrice, hasDesignatedDelivery) > 0 && (
                <div className="flex justify-between text-sm text-orange-600">
                  <span>도착시간 지정</span>
                  <span>+{formatPrice(calcDeliveryTimeSurcharge(priceBreakdown.totalPrice, hasDesignatedDelivery))}</span>
                </div>
              )}
              <Separator className="my-2" />
              <div className="flex justify-between font-bold">
                <span className="text-[#1A3A5C]">합계</span>
                <span className="text-[#4DA3FF]">{formatPrice(calcFinalPrice(priceBreakdown.totalPrice, flexCount, hasDesignatedDelivery, pickupDiscountRate, deliveryDiscountRate))}</span>
              </div>
            </div>
          ) : (
            <div className="font-medium text-gray-400">정보 입력 후 계산됩니다</div>
          )}
        </div>

        <Separator />

        {/* 일정 */}
        <div>
          <div className="text-xs text-gray-500 mb-1">배송 일정</div>
          {scheduleDate ? (
            <div className="font-medium text-gray-900">
              {scheduleDate}
              {getScheduleLabel() && (
                <div className="text-sm text-gray-600 mt-0.5">{getScheduleLabel()}</div>
              )}
            </div>
          ) : (
            <div className="font-medium text-gray-400">일정을 선택해주세요</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
